import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";

const CHANNEL_ICONS = {
  general: "mdi-text-box-outline",
  gemeindeblatt: "mdi-book-open-page-variant-outline",
  web: "mdi-web",
  app: "mdi-cellphone"
};
const DEFAULT_CHANNEL_ICON = "mdi-share-variant-outline";

export default {
  name: "BatchAssignChannelDialog",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    excludedLockedCount: {
      type: Number,
      default: 0
    },
    excludedDeletedCount: {
      type: Number,
      default: 0
    },
    principalId: {
      type: String,
      required: true
    },
    newsApiUrl: {
      type: String,
      required: true
    },
    channelsConfig: {
      type: Array,
      default: () => []
    },
    issues: {
      type: Array,
      default: () => []
    },
    features: {
      type: Array,
      default: () => []
    },
    featureGroups: {
      type: Array,
      default: () => []
    },
    appContentTypes: {
      type: Array,
      default: () => []
    },
    mainPeacock: {
      type: String,
      default: "#00636C"
    }
  },
  data() {
    return {
      phase: "select",
      action: "add",
      channelPicks: {},
      requiredCursor: 0,
      requiredQueue: [],
      currentFieldDefs: [],
      requiredValues: {},
      skippedItemIds: [],
      anySkipped: false,
      saving: false,
      processed: []
    };
  },
  computed: {
    internalOpen: {
      get() {
        return this.isOpen;
      },
      set(value) {
        if (!value) {
          this.closeDialog();
        }
        this.$emit("update:isOpen", value);
      }
    },
    subline() {
      let text = `${this.items.length} ausgewählte Nachricht${
        this.items.length === 1 ? "" : "en"
      }`;
      const excluded = [];
      if (this.excludedLockedCount) {
        excluded.push(`${this.excludedLockedCount} gesperrt`);
      }
      if (this.excludedDeletedCount) {
        excluded.push(`${this.excludedDeletedCount} gelöscht`);
      }
      if (excluded.length) {
        text += ` · ${excluded.join(" · ")}`;
      }
      return text;
    },
    isRemoveAction() {
      return this.action === "remove";
    },
    actionOptions() {
      return [
        { key: "add", label: "Hinzufügen" },
        { key: "remove", label: "Entfernen" }
      ].map(a => ({
        ...a,
        on: this.action === a.key,
        pick: () => {
          this.action = a.key;
        }
      }));
    },
    pickedKeys() {
      return Object.keys(this.channelPicks).filter(k => this.channelPicks[k]);
    },
    channelOptions() {
      return this.channelsConfig.map(channel => {
        const on = !!this.channelPicks[channel.value];
        return {
          value: channel.value,
          label: channel.title,
          icon: CHANNEL_ICONS[channel.value] || DEFAULT_CHANNEL_ICON,
          on,
          box: on ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline",
          color: on ? this.mainPeacock : "#CDCDD2",
          toggle: () => this.$set(this.channelPicks, channel.value, !on)
        };
      });
    },
    cIssuesForSelect() {
      return this.issues.map(issue => ({
        value: issue._id,
        label:
          issue.closed && !issue.title.includes("(geschlossen)")
            ? `${issue.title} (geschlossen)`
            : issue.title,
        disabled: !!issue.closed
      }));
    },
    itemsNeedingRequired() {
      if (this.isRemoveAction) return [];
      return this.items.filter(item => this.requiredFieldDefs(item).length);
    },
    needsRequiredPhase() {
      return !this.isRemoveAction && !!this.itemsNeedingRequired.length;
    },
    ctaLabel() {
      if (this.needsRequiredPhase) return "Weiter zu Pflichtangaben";
      return this.isRemoveAction ? "Kanäle entfernen" : "Kanäle zuordnen";
    },
    assignHint() {
      return this.isRemoveAction
        ? "Der Kanal wird bei allen Bildern der ausgewählten Nachrichten entfernt."
        : "Der Kanal wird bei allen Bildern der ausgewählten Nachrichten hinzugefügt.";
    },
    requiredItem() {
      const list = this.requiredQueue;
      if (!list.length) return null;
      return list[Math.min(this.requiredCursor, list.length - 1)];
    },
    requiredStepLabel() {
      return `Nachricht ${Math.min(
        this.requiredCursor + 1,
        this.requiredQueue.length
      )} von ${this.requiredQueue.length}`;
    },
    requiredChannelLabel() {
      return `Pflichtangaben für: ${this.pickedKeys
        .map(k => this.getChannelTitle(k))
        .join(", ")}`;
    },
    requiredStepDots() {
      return this.requiredQueue.map((item, i) => {
        if (i < this.requiredCursor) return this.mainPeacock;
        if (i === this.requiredCursor) return "#EBB104";
        return "#E0E0E0";
      });
    },
    requiredFieldsForCurrent() {
      return this.currentFieldDefs;
    },
    requiredNextLabel() {
      return this.requiredCursor + 1 >= this.requiredQueue.length
        ? "Speichern & abschließen"
        : "Übernehmen & weiter";
    },
    hasMissingRequiredSelections() {
      const item = this.requiredItem;
      if (!item) return false;
      return this.requiredFieldDefs(item).some(fieldDef =>
        this.isRequiredSelectionMissing(fieldDef)
      );
    },
    requiredNextDisabled() {
      return this.saving || this.hasMissingRequiredSelections;
    },
    doneTitle() {
      const savedCount = this.processed.filter(r => r.saved).length;
      return `${savedCount} von ${this.items.length} Nachrichten aktualisiert`;
    },
    doneHint() {
      let text = this.isRemoveAction
        ? "Die ausgewählten Kanäle wurden entfernt."
        : "Die ausgewählten Kanäle wurden zugeordnet.";
      if (this.anySkipped) {
        text += " Übersprungene Nachrichten wurden nicht bearbeitet.";
      }
      return text;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initialize();
      }
    }
  },
  methods: {
    initialize() {
      this.phase = "select";
      this.action = "add";
      this.channelPicks = {};
      this.requiredCursor = 0;
      this.requiredQueue = [];
      this.currentFieldDefs = [];
      this.requiredValues = {};
      this.skippedItemIds = [];
      this.anySkipped = false;
      this.saving = false;
      this.processed = [];
    },

    closeDialog() {
      this.$emit("close");
    },

    getChannelTitle(value) {
      return (
        this.channelsConfig.find(channel => channel.value === value)?.title ||
        value
      );
    },

    imagesWithChannel(item, channelValue) {
      return (item.images || []).filter(
        image =>
          Array.isArray(image?.channels) &&
          image.channels.includes(channelValue)
      );
    },

    appContentTypeById(id) {
      return this.appContentTypes.find(type => type.appdinx_id === id);
    },

    appChannelsForContentType(contentTypeId) {
      const contentType = this.appContentTypeById(contentTypeId);
      if (!contentType) return [];
      return this.features.filter(feature =>
        contentType.feature_ids?.includes(feature._id)
      );
    },

    isFeatureSelectableForCurrentUser(feature) {
      if (!feature.editable_by || feature.editable_by === "all") return true;
      const scopes = this.$store.state.currentUser?.scopes || [];
      if (feature.editable_by === "news_admin") {
        return (
          scopes.includes("role:admin") || scopes.includes("role:news_admin")
        );
      }
      if (feature.editable_by === "admin") {
        return scopes.includes("role:admin");
      }
      return true;
    },

    initialRequiredValuesFor(item) {
      return {
        issue_id: item.issue_id || "",
        appdinx_duplicate_content_type_id:
          item.appdinx_duplicate_content_type_id || "",
        appdinx_channels: Array.isArray(item.appdinx_channels)
          ? item.appdinx_channels.filter(channel => !!channel)
          : [],
        feature_ids: Array.isArray(item.feature_ids)
          ? Array.from(new Set(item.feature_ids.filter(id => !!id)))
          : []
      };
    },

    // constraints mirrored from NewsEditDialog.toggleChannel / the "Merkmale" panel:
    // - adding "gemeindeblatt" requires an Ausgabe (issue_id)
    // - adding "app" requires an App Inhaltstyp, and an App-Kanal when the
    //   content type offers more than one
    // - any required Merkmale-Gruppe applicable to the selected channel set
    //   needs at least one selected feature. In batch mode, these fields are
    //   always shown so existing values can be reviewed and overwritten.
    requiredFieldDefs(item) {
      if (this.isRemoveAction) return [];
      const vals =
        this.requiredValues[item._id] || this.initialRequiredValuesFor(item);
      const defs = [];

      if (this.pickedKeys.includes("gemeindeblatt")) {
        defs.push({
          key: "issue_id",
          label: `${this.getChannelTitle("gemeindeblatt")} Ausgabe (Pflicht)`,
          hint: "Nur offene Ausgaben; gesperrte Ausgaben werden übersprungen.",
          options: this.cIssuesForSelect,
          value: vals.issue_id || ""
        });
      }

      if (this.pickedKeys.includes("app")) {
        const contentTypeId = vals.appdinx_duplicate_content_type_id || "";
        defs.push({
          key: "appdinx_duplicate_content_type_id",
          label: "App Inhaltstyp (Pflicht)",
          hint: "Bestimmt die Zuordnung im App-Backend.",
          options: this.appContentTypes.map(type => ({
            value: type.appdinx_id,
            label: type.title
          })),
          value: contentTypeId
        });

        if (contentTypeId) {
          const appChannels = this.appChannelsForContentType(contentTypeId);
          const chosenChannels = Array.isArray(vals.appdinx_channels)
            ? vals.appdinx_channels
            : [];
          if (appChannels.length > 1) {
            const allowedChannelIds = new Set(
              appChannels.map(channel => channel.appdinx_channel_id)
            );
            const selectedChannels = chosenChannels.filter(channel =>
              allowedChannelIds.has(channel)
            );
            defs.push({
              key: "appdinx_channels",
              label: "App-Kanäle (Pflicht)",
              hint: "Push kann pro Nachricht abgewählt werden.",
              multi: true,
              options: appChannels.map(channel => ({
                value: channel.appdinx_channel_id,
                label: channel.title
              })),
              value: selectedChannels
            });
          }
        }
      }

      this.featureGroups
        .filter(
          fg =>
            fg.isRequired &&
            Array.isArray(fg.channels) &&
            fg.channels.some(channel => this.pickedKeys.includes(channel))
        )
        .forEach(fg => {
          const selectableFeatures = (fg.features || []).filter(feature =>
            this.isFeatureSelectableForCurrentUser(feature)
          );
          if (!selectableFeatures.length) return;
          defs.push({
            key: `feature_group.${fg._id}`,
            label: `${fg.title} (Pflicht)`,
            hint: `Mindestens ein Merkmal der Gruppe „${fg.title}“ auswählen.`,
            multi: true,
            featureGroup: true,
            options: selectableFeatures.map(feature => ({
              value: feature._id,
              label: feature.title
            })),
            value: (vals.feature_ids || []).filter(id =>
              selectableFeatures.some(feature => feature._id === id)
            )
          });
        });

      return defs;
    },

    isRequiredSelectionMissing(fieldDef) {
      if (fieldDef.multi) {
        return !Array.isArray(fieldDef.value) || !fieldDef.value.length;
      }
      return !fieldDef.value;
    },

    setRequiredValue(fieldDef, value) {
      const item = this.requiredItem;
      if (!item) return;
      const current = { ...(this.requiredValues[item._id] || {}) };

      if (fieldDef.key === "issue_id") {
        current.issue_id = value;
      } else if (fieldDef.key === "appdinx_duplicate_content_type_id") {
        current.appdinx_duplicate_content_type_id = value;
        const appChannels = this.appChannelsForContentType(value);
        current.appdinx_channels =
          appChannels.length === 1 ? [appChannels[0].appdinx_channel_id] : [];
      } else if (fieldDef.key === "appdinx_channels") {
        current.appdinx_channels = value;
      } else if (fieldDef.featureGroup) {
        const otherFeatureIds = (current.feature_ids || []).filter(
          id => !fieldDef.options.some(option => option.value === id)
        );
        current.feature_ids = [...otherFeatureIds, ...value];
      }

      this.$set(this.requiredValues, item._id, current);

      // Recompute the field defs, but keep any field already on screen visible
      // (with its latest value/options) instead of letting it disappear the
      // moment it becomes fulfilled — only reveal genuinely new fields (e.g.
      // "App-Kanäle" once a content type is chosen).
      const freshDefs = this.requiredFieldDefs(item);
      const freshByKey = new Map(freshDefs.map(def => [def.key, def]));
      const seenKeys = new Set(this.currentFieldDefs.map(def => def.key));
      this.currentFieldDefs = this.currentFieldDefs
        .map(def =>
          def.key === fieldDef.key
            ? freshByKey.get(def.key) || { ...def, value }
            : def
        )
        .concat(freshDefs.filter(def => !seenKeys.has(def.key)));
    },

    async startAssign() {
      if (!this.pickedKeys.length) return;
      if (this.isRemoveAction) {
        await this.applyAll();
        return;
      }
      if (this.needsRequiredPhase) {
        this.requiredQueue = [...this.itemsNeedingRequired];
        this.requiredValues = this.requiredQueue.reduce((values, item) => {
          values[item._id] = this.initialRequiredValuesFor(item);
          return values;
        }, {});
        this.requiredCursor = 0;
        this.phase = "required";
        this.loadCurrentFieldDefs();
      } else {
        await this.applyAll();
      }
    },

    reqNext() {
      if (this.requiredNextDisabled) return;
      const item = this.requiredItem;
      if (item && this.skippedItemIds.includes(item._id)) {
        this.skippedItemIds = this.skippedItemIds.filter(id => id !== item._id);
        this.anySkipped = this.skippedItemIds.length > 0;
      }
      this.advanceRequiredCursorOrApply();
    },

    advanceRequiredCursorOrApply() {
      if (this.requiredCursor + 1 >= this.requiredQueue.length) {
        this.applyAll();
      } else {
        this.requiredCursor += 1;
        this.loadCurrentFieldDefs();
      }
    },

    reqSkip() {
      const item = this.requiredItem;
      if (item && !this.skippedItemIds.includes(item._id)) {
        this.skippedItemIds = [...this.skippedItemIds, item._id];
      }
      this.anySkipped = this.skippedItemIds.length > 0;
      this.advanceRequiredCursorOrApply();
    },

    reqPrev() {
      this.requiredCursor = Math.max(0, this.requiredCursor - 1);
      this.loadCurrentFieldDefs();
    },

    loadCurrentFieldDefs() {
      const item = this.requiredItem;
      this.currentFieldDefs = item ? this.requiredFieldDefs(item) : [];
    },

    reqApplyAll() {
      const source = { ...(this.requiredValues[this.requiredItem?._id] || {}) };
      this.requiredQueue.forEach(item => {
        const target = { ...(this.requiredValues[item._id] || {}) };
        this.currentFieldDefs.forEach(fieldDef => {
          if (fieldDef.key === "issue_id") {
            target.issue_id = source.issue_id || "";
            return;
          }
          if (fieldDef.key === "appdinx_duplicate_content_type_id") {
            target.appdinx_duplicate_content_type_id =
              source.appdinx_duplicate_content_type_id || "";
            const appChannels = this.appChannelsForContentType(
              target.appdinx_duplicate_content_type_id
            );
            target.appdinx_channels =
              appChannels.length === 1
                ? [appChannels[0].appdinx_channel_id]
                : [];
            return;
          }
          if (fieldDef.key === "appdinx_channels") {
            target.appdinx_channels = Array.isArray(source.appdinx_channels)
              ? [...source.appdinx_channels]
              : [];
            return;
          }
          if (fieldDef.featureGroup) {
            const groupFeatureIds = new Set(
              fieldDef.options.map(option => option.value)
            );
            const sourceGroupFeatureIds = (
              source.feature_ids || []
            ).filter(id => groupFeatureIds.has(id));
            const otherFeatureIds = (target.feature_ids || []).filter(
              id => !groupFeatureIds.has(id)
            );
            target.feature_ids = [...otherFeatureIds, ...sourceGroupFeatureIds];
          }
        });
        this.$set(this.requiredValues, item._id, target);
      });
      this.applyAll();
    },

    imagesWithChannelToggled(item, add) {
      let touched = false;
      const images = (item.images || []).map(image => {
        if (!Array.isArray(image.channels)) return image;
        let channels = image.channels;
        this.pickedKeys.forEach(key => {
          const has = channels.includes(key);
          if (add && !has) {
            channels = [...channels, key];
            touched = true;
          } else if (!add && has) {
            channels = channels.filter(
              channel => channel?.length && channel !== key
            );
            touched = true;
          }
        });
        return channels === image.channels ? image : { ...image, channels };
      });
      return { images, touched };
    },

    buildPayload(item) {
      const vals = this.requiredValues[item._id] || {};
      const payload = {};

      let channels = [...(item.channels || [])];
      if (this.isRemoveAction) {
        channels = channels.filter(
          channel => channel?.length && !this.pickedKeys.includes(channel)
        );
      } else {
        this.pickedKeys.forEach(key => {
          if (!channels.includes(key)) channels.push(key);
        });
      }
      payload.channels = channels;

      const { images, touched } = this.imagesWithChannelToggled(
        item,
        !this.isRemoveAction
      );
      if (touched) payload.images = images;

      if (!this.isRemoveAction) {
        const alt2 = { ...(item.alternative_content_2 || { web: "" }) };
        const alt3 = { ...(item.alternative_content_3 || { web: "" }) };
        let alt2Changed = false;
        let alt3Changed = false;
        this.pickedKeys.forEach(key => {
          if (!(key in alt2)) {
            alt2[key] = "";
            alt2Changed = true;
          }
          if (!(key in alt3)) {
            alt3[key] = "";
            alt3Changed = true;
          }
        });
        if (alt2Changed) payload.alternative_content_2 = alt2;
        if (alt3Changed) payload.alternative_content_3 = alt3;

        if (this.pickedKeys.includes("gemeindeblatt")) {
          payload.issue_id = vals.issue_id || "";
        }
        if (this.pickedKeys.includes("app")) {
          const contentTypeId = vals.appdinx_duplicate_content_type_id || "";
          const appChannels = this.appChannelsForContentType(contentTypeId);
          const allowedChannelIds = new Set(
            appChannels.map(channel => channel.appdinx_channel_id)
          );
          const selectedAppChannels = Array.isArray(vals.appdinx_channels)
            ? vals.appdinx_channels.filter(
                channel => !!channel && allowedChannelIds.has(channel)
              )
            : [];
          payload.appdinx_duplicate_content_type_id = contentTypeId;
          payload.appdinx_channels =
            appChannels.length === 1
              ? [appChannels[0].appdinx_channel_id]
              : selectedAppChannels;
        }
        if (
          Object.prototype.hasOwnProperty.call(this.requiredValues, item._id)
        ) {
          payload.feature_ids = Array.from(new Set(vals.feature_ids || []));
        }
      }

      return payload;
    },

    async persistItem(item) {
      const payload = this.buildPayload(item);
      const record = { item, payload, saved: false };
      try {
        const headers = getTokenHeader();
        await axios.put(
          `${this.newsApiUrl}news/`,
          {
            id: item._id,
            _id: item._id,
            principal_id: item.principal_id,
            ...payload
          },
          { headers }
        );
        record.saved = true;
      } catch (e) {
        console.error(e);
        this.$emit("error", {
          text: `„${item.title}“ konnte nicht gespeichert werden.`,
          error: e
        });
      }
      return record;
    },

    async applyAll() {
      this.saving = true;
      this.processed = [];
      const skippedItemIds = new Set(this.skippedItemIds);
      for (const item of this.items) {
        if (skippedItemIds.has(item._id)) continue;
        const record = await this.persistItem(item);
        this.processed.push(record);
      }
      this.saving = false;
      this.phase = "done";
      this.emitSavedSummary();
    },

    emitSavedSummary() {
      const saved = this.processed.filter(r => r.saved);
      if (!saved.length) return;
      this.$emit("saved", {
        savedCount: saved.length,
        items: saved.map(r => ({ ...r.item, ...r.payload }))
      });
    },

    finish() {
      this.$emit("close");
    }
  }
};
